import { z } from "zod";

/**
 * Le categorie di spesa.
 *
 * Ventidue, un livello solo. Il valore salvato è lo **slug in inglese**:
 * l'etichetta italiana è una decisione di interfaccia, e legarci il database
 * significherebbe una migration il giorno che cambia una parola.
 *
 * L'icona **deriva dalla categoria**, non si sceglie: due campi indipendenti
 * potrebbero contraddirsi — una spesa in «Trasporti» con l'icona della pizza —
 * e nelle statistiche conterebbe una cosa mentre in lista ne sembra un'altra.
 */
export const EXPENSE_CATEGORIES = [
  "groceries",
  "dining",
  "utilities",
  "home",
  "furniture",
  "subscriptions",
  "cleaning",
  "gifts",
  "entertainment",
  "shopping",
  "transport",
  "lodging",
  "health",
  "electronics",
  "maintenance",
  "pets",
  "taxes",
  "sports",
  "culture",
  "travel",
  "children",
  "other",
] as const;

export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

/** Quando non si sceglie niente. Categorizzare resta facoltativo. */
export const DEFAULT_CATEGORY: ExpenseCategory = "other";

export const expenseCategorySchema = z.enum(EXPENSE_CATEGORIES);

export interface CategoryMeta {
  label: string;
  /** Nome dell'icona nel registro di `@qota/ui`. */
  icon: string;
}

export const CATEGORY_META: Record<ExpenseCategory, CategoryMeta> = {
  groceries: { label: "Spesa", icon: "shopping-cart" },
  dining: { label: "Ristoranti e bar", icon: "utensils" },
  utilities: { label: "Bollette", icon: "zap" },
  home: { label: "Casa", icon: "house" },
  furniture: { label: "Arredamento", icon: "sofa" },
  subscriptions: { label: "Abbonamenti", icon: "repeat" },
  cleaning: { label: "Cura e pulizia", icon: "sparkles" },
  gifts: { label: "Regali", icon: "gift" },
  entertainment: { label: "Svago", icon: "party-popper" },
  shopping: { label: "Shopping", icon: "shopping-bag" },
  transport: { label: "Trasporti", icon: "car" },
  lodging: { label: "Alloggio", icon: "bed-double" },
  health: { label: "Salute", icon: "heart-pulse" },
  electronics: { label: "Elettronica", icon: "laptop" },
  maintenance: { label: "Manutenzione", icon: "wrench" },
  pets: { label: "Animali", icon: "paw-print" },
  taxes: { label: "Tasse e assicurazioni", icon: "landmark" },
  sports: { label: "Sport", icon: "dumbbell" },
  culture: { label: "Cultura", icon: "book-open" },
  travel: { label: "Viaggi", icon: "plane" },
  children: { label: "Bambini", icon: "baby" },
  other: { label: "Altro", icon: "receipt" },
};

export function categoryLabel(category: ExpenseCategory): string {
  return CATEGORY_META[category].label;
}

export function categoryIcon(category: ExpenseCategory): string {
  return CATEGORY_META[category].icon;
}

/**
 * Indizi per indovinare la categoria dalla descrizione.
 *
 * Nascono da un export reale di tredici mesi: il 42% delle spese ripete una
 * descrizione già usata, e nello stesso export la stessa voce era finita in
 * categorie diverse di volta in volta. Suggerire toglie di mezzo la maggior
 * parte di quelle incoerenze senza chiedere niente a nessuno.
 *
 * L'ordine conta: vince il primo che aggancia, quindi le voci specifiche
 * stanno prima di quelle generiche.
 */
const HINTS: { category: ExpenseCategory; pattern: RegExp }[] = [
  // Prima le ricorrenze mensili, che sono la maggioranza delle spese ripetute.
  { category: "utilities", pattern: /bolletta|luce|enel|gas|bombola|metano|internet|fibra|adsl|telefon|wind|tim|vodafone|iliad|kw/i },
  { category: "subscriptions", pattern: /abbonament|verisure|allarme|spotify|netflix|disney|prime|canone/i },
  { category: "home", pattern: /condominio|affitto|mutuo|spese condominiali|portier/i },

  // Le catene specifiche PRIMA della spesa generica: «Spesa Tigotà» è un
  // negozio di detersivi, non un supermercato, e contiene comunque «spesa».
  { category: "cleaning", pattern: /tigot|acqua e sapone|detersiv|pulizi|igien|carta igienica|asciugamani|ammorbident/i },
  { category: "groceries", pattern: /spesa|conad|coop|esselunga|lidl|eurospin|carrefour|supermercat|maxicasa|alimentar|pane|frutta/i },
  { category: "dining", pattern: /cena|pranzo|ristorante|pizzeria|trattoria|osteria|bar\b|caff|colazione|aperitivo|burger|sushi|gelat|pasticc|brioche/i },

  { category: "furniture", pattern: /divano|comodin|armadio|tavolo|sedi[ae]|letto|mobil|aspirapolvere|lavatrice|frigo|forno|lampad|ikea|materasso|tv\b/i },
  { category: "electronics", pattern: /amazon|telefono|smartphone|computer|notebook|tablet|cuffie|caricabatt|monitor|stampante/i },
  { category: "maintenance", pattern: /idraulic|elettricist|riparaz|manutenz|tagliando|revision|filtri|caldaia|fabbro/i },

  { category: "taxes", pattern: /tari|imu|bollo|assicurazion|tassa|imposta|f24/i },
  { category: "transport", pattern: /benzina|carburante|gasolio|diesel|autostrad|pedagg|parcheggi|taxi|uber|autobus|metro|lavaggio|auto\b/i },
  { category: "travel", pattern: /aereo|volo|trenitalia|italo|treno|biglietto (aereo|treno)|traghetto|noleggio/i },
  { category: "lodging", pattern: /hotel|albergo|b&b|airbnb|agriturismo|ostello|pernott/i },

  { category: "health", pattern: /farmac|pillola|medicin|dottor|dentist|visita|analisi|ospedale|ottic|occhiali|integrator/i },
  { category: "pets", pattern: /veterinari|crocchett|cane\b|gatt|toelett|animal/i },
  { category: "children", pattern: /asilo|nido|pannolin|bambin|scuola|babysitter/i },

  { category: "gifts", pattern: /regal|buono|bomboniera|compleanno(?!.*cena)/i },
  { category: "culture", pattern: /libr|museo|mostra|cinema|teatro|\bcd\b|vinile/i },
  { category: "sports", pattern: /palestra|padel|piscina|calcetto|tennis|corso\b|attrezzatura/i },
  { category: "entertainment", pattern: /concerto|biglietti|festival|discotec|spettacol|gioc/i },
  { category: "shopping", pattern: /vestit|scarpe|abbigliament|maglia|pantalon|zara|h&m|decathlon/i },
];

/**
 * Indovina la categoria da una descrizione. `null` quando non c'è un indizio
 * affidabile — meglio lasciare «Altro» che sbagliare di sicuro.
 */
export function suggestCategory(description: string): ExpenseCategory | null {
  const text = description.trim();
  if (text.length < 2) return null;

  for (const hint of HINTS) {
    if (hint.pattern.test(text)) return hint.category;
  }

  return null;
}
